import { useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Box, Container, Typography, Button } from '@mui/material'
import { useAuthStore } from '../stores/auth-store'
import { SiteNav } from '../components/site/SiteNav'
import { SiteFooter } from '../components/site/SiteFooter'

/**
 * Household invitation landing page.
 *
 * The invitation email links here and this route did not exist, so an invite
 * was a dead end for the person receiving it.
 *
 * This page deliberately does NOT auto-accept. Joining a household means
 * sharing financial data, and that should be a decision someone makes on a
 * page that says so — not a side effect of clicking a link in an email, which
 * is also what a phishing attempt looks like.
 */
export const Route = createFileRoute('/invite')({
  component: Invite,
})

function Invite() {
  const { isAuthenticated } = useAuthStore()
  const [token, setToken] = useState('')

  useEffect(() => {
    setToken(new URLSearchParams(window.location.search).get('token') ?? '')
  }, [])

  return (
    <Box sx={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <SiteNav />
      <Container maxWidth="sm" sx={{ flex: 1, py: { xs: 8, md: 12 } }}>
        <Typography component="h1" sx={{
          fontSize: '1.9rem', fontWeight: 700, letterSpacing: '-0.03em', mb: 1.5,
        }}>
          {token ? 'You have been invited to a household' : 'No invitation token'}
        </Typography>

        {!token ? (
          <>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 4 }}>
              This page needs the link from your invitation email.
            </Typography>
            <Button component={Link} to="/login" variant="contained">Go to sign in</Button>
          </>
        ) : (
          <>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 2, lineHeight: 1.7 }}>
              Joining a household means sharing accounts, balances and bills
              with its members. Review the invitation from your household page
              before accepting.
            </Typography>
            <Typography sx={{ color: 'var(--text-faint)', mb: 4, lineHeight: 1.7 }}>
              {isAuthenticated
                ? 'Open your household page to accept or decline.'
                : 'Sign in with the address the invitation was sent to, then open your household page.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {isAuthenticated ? (
                <Button component={Link} to="/household" variant="contained">
                  Open household
                </Button>
              ) : (
                <>
                  <Button component={Link} to="/login" variant="contained">Sign in</Button>
                  <Button component={Link} to="/register" variant="outlined">
                    Create an account
                  </Button>
                </>
              )}
            </Box>
          </>
        )}
      </Container>
      <SiteFooter />
    </Box>
  )
}
